<script setup lang="ts">
import InputNumber from 'primevue/inputnumber';
import {
  clampHourForFormat,
  clampMinute,
  type DateTimePickerHourFormat,
} from '../utils/datetime-picker-time';

const props = withDefaults(
  defineProps<{
    hourFormat?: DateTimePickerHourFormat;
    disabled?: boolean;
    size?: 'small' | 'large';
    label?: string;
  }>(),
  {
    hourFormat: '12',
    disabled: false,
  },
);

const hour = defineModel<number>('hour', { required: true });
const minute = defineModel<number>('minute', { required: true });
const pm = defineModel<boolean>('pm', { required: true });

function onHourUpdate(value: number | null) {
  hour.value = clampHourForFormat(value ?? 0, props.hourFormat);
}

function onMinuteUpdate(value: number | null) {
  minute.value = clampMinute(value ?? 0);
}

function setPm(nextPm: boolean) {
  pm.value = nextPm;
}
</script>

<template>
  <div class="flex flex-col items-center gap-1">
    <span
      v-if="label"
      class="text-xs font-medium text-surface-500"
    >
      {{ label }}
    </span>
    <div
      class="flex flex-wrap items-center justify-center gap-2"
      data-gpx-datetime-time-row
      @mousedown.stop
      @click.stop
    >
      <InputNumber
        :model-value="hour"
        :min="hourFormat === '24' ? 0 : 1"
        :max="hourFormat === '24' ? 23 : 12"
        :disabled="disabled"
        :size="size"
        show-buttons
        button-layout="horizontal"
        :input-class="['w-12 text-center', size === 'small' ? 'text-sm' : '']"
        aria-label="Hour"
        @update:model-value="onHourUpdate"
      />
      <span class="text-surface-500 select-none" aria-hidden="true">:</span>
      <InputNumber
        :model-value="minute"
        :min="0"
        :max="59"
        :disabled="disabled"
        :size="size"
        show-buttons
        button-layout="horizontal"
        :input-class="['w-12 text-center', size === 'small' ? 'text-sm' : '']"
        aria-label="Minute"
        @update:model-value="onMinuteUpdate"
      />
      <div
        v-if="hourFormat === '12'"
        class="flex overflow-hidden rounded-md border border-surface-300"
        role="group"
        aria-label="AM or PM"
      >
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium transition-colors"
          :class="
            !pm
              ? 'bg-primary text-primary-contrast'
              : 'bg-surface-0 text-surface-700 hover:bg-surface-100'
          "
          :disabled="disabled"
          @click="setPm(false)"
        >
          AM
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium transition-colors"
          :class="
            pm
              ? 'bg-primary text-primary-contrast'
              : 'bg-surface-0 text-surface-700 hover:bg-surface-100'
          "
          :disabled="disabled"
          @click="setPm(true)"
        >
          PM
        </button>
      </div>
    </div>
  </div>
</template>
